import React, { useState } from "react";
import { toMeter } from "../../../utils/unitConverter";
import ReadinessScore from "./ReadinessScore";
import CalculatorNotes from "./CalculatorNotes";
import QuickChecklist, { initialChecklist } from "./QuickChecklist";
import DownloadPDF from "./DownloadConcretePDF";
import CalculatorInquiryModal from "../Common/CalculatorInquiryModal";

const StairCalculator = () => {
  const [form, setForm] = useState({
    run: "",
    runUnit: "cm",
    rise: "",
    riseUnit: "cm",
    width: "",
    widthUnit: "cm",
    platformDepth: "",
    platformDepthUnit: "cm",
    steps: 1,
  });

  const [result, setResult] = useState(null);
  const [checklist, setChecklist] = useState(initialChecklist);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (
      ["run", "rise", "width", "platformDepth", "steps"].includes(name) &&
      value !== "" &&
      !/^\d*\.?\d*$/.test(value)
    ) {
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const calculate = () => {
    const run = toMeter(parseFloat(form.run), form.runUnit);
    const rise = toMeter(parseFloat(form.rise), form.riseUnit);
    const width = toMeter(parseFloat(form.width), form.widthUnit);
    const platformDepth = toMeter(
      parseFloat(form.platformDepth),
      form.platformDepthUnit,
    );
    const steps = parseFloat(form.steps);

    const totalRise = steps * rise;
    const totalRun = (steps - 1) * run;

    const stairVolume = 0.5 * width * totalRun * totalRise;

    const platformVolume = width * platformDepth * rise;

    const volumeM3 = stairVolume + platformVolume;

    const volumeFt3 = volumeM3 * 35.3147;
    const volumeYd3 = volumeM3 * 1.30795;

    const weightKg = volumeM3 * 2130;
    const weightLbs = weightKg * 2.20462;

    const bags60 = weightLbs / 60;
    const bags80 = weightLbs / 80;

    setResult({
      volumeM3,
      volumeFt3,
      volumeYd3,
      weightKg,
      weightLbs,
      bags60,
      bags80,
    });
  };

  const clearForm = () => {
    setForm({
      run: "",
      runUnit: "cm",
      rise: "",
      riseUnit: "cm",
      width: "",
      widthUnit: "cm",
      platformDepth: "",
      platformDepthUnit: "cm",
      steps: 1,
    });
    setResult(null);
  };

  const checkedCount = checklist.filter((item) => item.checked).length;

  const scoreMap = {
    0: 0,
    1: 20,
    2: 35,
    3: 45,
    4: 55,
    5: 65,
    6: 75,
    7: 82,
    8: 89,
    9: 95,
  };

  const readinessScore = scoreMap[checkedCount];

  const calculatorName = "Stairs Calculator";

  return (
    <div className=" p-6 bg-white rounded-2xl shadow-xl border">
      <h2 className="text-2xl font-semibold font-heading text-start text-gray-800 mb-8">
        {calculatorName}
      </h2>

      <div className="flex flex-col sm:flex-row gap-8 items-start">
        {/* LEFT FORM */}
        <div className="space-y-3 w-full sm:w-1/2">
          {[
            {
              label: "Run",
              name: "run",
              unit: "runUnit",
            },
            {
              label: "Rise",
              name: "rise",
              unit: "riseUnit",
            },
            {
              label: "Width",
              name: "width",
              unit: "widthUnit",
            },
            {
              label: "Platform Depth",
              name: "platformDepth",
              unit: "platformDepthUnit",
            },
          ].map((field, index) => (
            <div key={index} className="space-y-1">
              <label className="text-gray-600 font-medium">{field.label}</label>

              <div className="flex gap-2">
                <input
                  type="text"
                  name={field.name}
                  value={form[field.name]}
                  onChange={handleChange}
                  placeholder={`Enter ${field.label}`}
                  className="w-full border px-3 py-2 rounded-lg focus:ring-2 focus:ring-btnColor outline-none"
                />

                <select
                  name={field.unit}
                  value={form[field.unit]}
                  onChange={handleChange}
                  className="border px-2 rounded-lg bg-gray-50"
                >
                  <option value="m">m</option>
                  <option value="cm">cm</option>
                  <option value="mm">mm</option>
                  <option value="ft">ft</option>
                  <option value="yd">yd</option>
                  <option value="in">in</option>
                </select>
              </div>
            </div>
          ))}

          {/* Quantity */}
          <div>
            <label className="text-gray-600 font-medium">Steps</label>
            <input
              type="text"
              name="steps"
              value={form.steps}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded-lg focus:ring-2 focus:ring-btnColor outline-none mt-1"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={calculate}
              className="flex-1 bg-btnColor text-white py-3 rounded-xl shadow-md hover:bg-btnHoverColor  border border-btnColor transition"
            >
              Action
            </button>

            <button
              onClick={clearForm}
              className="flex-1 bg-gray-200 py-3 rounded-xl hover:bg-gray-700 hover:text-white transition"
            >
              Clear
            </button>
          </div>
        </div>
        <div className="flex flex-col w-full sm:w-1/2 items-center justify-between">
          {/* CENTER SVG DIAGRAM */}
          <div className="flex justify-center items-center">
            <svg width="320" height="260" viewBox="0 0 320 260">
              <defs>
                {/* Blue Arrow */}
                <marker
                  id="arrowBlue"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <path d="M0,0 L6,3 L0,6 Z" fill="#2563eb" />
                </marker>

                {/* Red Arrow */}
                <marker
                  id="arrowRed"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <path d="M0,0 L6,3 L0,6 Z" fill="#ef4444" />
                </marker>
              </defs>

              {/* -------- STAIR SHAPE -------- */}
              <path
                d="
        M40 200
        L100 200 L100 170
        L150 170 L150 140
        L200 140 L200 110
        L250 110 L250 80
        L300 80
        L300 200
        Z"
                fill="#e5e7eb"
                stroke="#6b7280"
                strokeWidth="1.2"
              />

              {/* -------- STEP NUMBERS -------- */}
              <text x="110" y="190" fontSize="12" fill="#1f2937">
                1
              </text>
              <text x="160" y="160" fontSize="12" fill="#1f2937">
                2
              </text>
              <text x="210" y="130" fontSize="12" fill="#1f2937">
                3
              </text>
              <text x="260" y="100" fontSize="12" fill="#1f2937">
                4
              </text>
              <text x="285" y="70" fontSize="12" fill="#1f2937">
                5
              </text>

              {/* -------- RUN (Blue) -------- */}
              <line
                x1="200"
                y1="120"
                x2="250"
                y2="120"
                stroke="#2563eb"
                strokeWidth="1.2"
                markerStart="url(#arrowBlue)"
                markerEnd="url(#arrowBlue)"
              />
              <text
                x="225"
                y="110"
                fontSize="12"
                fill="#2563eb"
                textAnchor="middle"
              >
                Run
              </text>

              {/* -------- RISE (Red) -------- */}
              <line
                x1="255"
                y1="110"
                x2="255"
                y2="140"
                stroke="#ef4444"
                strokeWidth="1.2"
                markerStart="url(#arrowRed)"
                markerEnd="url(#arrowRed)"
              />
              <text
                x="265"
                y="130"
                fontSize="12"
                fill="#ef4444"
                transform="rotate(90 265,130)"
              >
                Rise
              </text>

              {/* -------- WIDTH (Blue) -------- */}
              <line
                x1="40"
                y1="215"
                x2="300"
                y2="215"
                stroke="#2563eb"
                strokeWidth="1.2"
                markerStart="url(#arrowBlue)"
                markerEnd="url(#arrowBlue)"
              />
              <text
                x="170"
                y="235"
                fontSize="12"
                fill="#2563eb"
                textAnchor="middle"
              >
                Width
              </text>

              {/* -------- PLATFORM DEPTH (Blue) -------- */}
              <line
                x1="250"
                y1="60"
                x2="300"
                y2="60"
                stroke="#2563eb"
                strokeWidth="1.2"
                markerStart="url(#arrowBlue)"
                markerEnd="url(#arrowBlue)"
              />
              <text
                x="275"
                y="50"
                fontSize="12"
                fill="#2563eb"
                textAnchor="middle"
              >
                Platform Depth
              </text>
            </svg>
          </div>

          {/* RIGHT RESULT */}
          <div className="w-full mt-5">
            {result && (
              <div className="bg-gray-50 p-4 rounded-xl shadow-inner border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-1">
                  Result
                </h3>

                <div className="space-y-1 text-gray-700 text-sm">
                  <div>
                    <p className="font-medium">
                      Volume:{" "}
                      <span className="font-semibold">
                        {result.volumeM3.toFixed(2)} Cubic meters
                      </span>
                    </p>
                    <p className="text-gray-500 ml-1">
                      {/* {result.volumeYd3.toFixed(2)} Cubic Yards |{" "} */}
                      {result.volumeFt3.toFixed(2)} Cubic Feet
                    </p>
                  </div>

                  <div className="pt-3 border-t">
                    <p className="text-gray-500 mb-1">
                      Using density: 2130 kg/m³
                      {/* (133 lbs/ft³) */}
                    </p>
                    <p>
                      <span className="font-medium">Weight:</span>{" "}
                      {result.weightKg.toFixed(2)} kg
                      {/* {result.weightLbs.toFixed(2)} lbs */}
                    </p>
                  </div>

                  {/* <div className="pt-3 border-t">
                    <p>60 lb bags: {result.bags60.toFixed(2)}</p>
                    <p>80 lb bags: {result.bags80.toFixed(2)}</p>
                  </div> */}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {result && (
        <div className="mt-8">
          {/* Three Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div>
              <QuickChecklist
                checklist={checklist}
                setChecklist={setChecklist}
              />
              <ReadinessScore score={readinessScore} />
            </div>

            <div>
              <CalculatorNotes />
              {/* Buttons */}
              <div className="mt-10 sm:mt-16 flex flex-col items-center gap-4">
                {allowDownload ? (
                  <DownloadPDF
                    calculatorName={calculatorName}
                    result={result}
                    checklist={checklist}
                    score={readinessScore}
                    phone="+91 9124001120"
                    clearForm={clearForm}
                    autoDownload={allowDownload}
                    onAutoDownloadComplete={() => setAllowDownload(false)}
                  />
                ) : (
                  <button
                    className="px-16 sm:px-24 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    onClick={() => setShowInquiryModal(true)}
                  >
                    Generate PDF
                  </button>
                )}
                <a
                  href="tel:+919124001120"
                  className="bg-btnColor text-white px-12 sm:px-20 py-3 rounded-lg hover:bg-btnHoverColor transition"
                >
                  📞 Call for Enquiry
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      {showInquiryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg overflow-hidden w-full max-w-md relative">
            <button
              onClick={() => setShowInquiryModal(false)}
              className="absolute top-3 right-3 text-gray-500"
            >
              ✕
            </button>

            <CalculatorInquiryModal
              calculatorName={calculatorName}
              onSuccess={() => {
                setShowInquiryModal(false);
                setAllowDownload(true);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StairCalculator;
